import { Component, DestroyRef, ElementRef, effect, inject, output, signal, viewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'
import { NgClass } from '@angular/common'
import { TextFieldModule } from '@angular/cdk/text-field'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'

import { finalize } from 'rxjs/operators'

import { ChatState } from '@states/chat-state'
import { ChatService } from '@services/chat-service'
import { OperatorDataState } from '@states/operator-data'
import { ConversationTypeIconPipe } from '@pipes/conversation-type-icon-pipe'
import { ConversationTypeLabelPipe } from '@pipes/conversation-type-label-pipe'
import { FormatChatTimePipe } from '@pipes/format-chat-time-pipe'

@Component({
	selector: 'chat-message-view',
	templateUrl: './chat-message-view.html',
	styleUrl: './chat-message-view.scss',
	standalone: true,
	imports: [
		NgClass,
		ReactiveFormsModule,
		TextFieldModule,
		MatButtonModule,
		MatIconModule,
		MatInputModule,
		MatFormFieldModule,
		MatProgressSpinnerModule,
		MatTooltipModule,
		ConversationTypeIconPipe,
		ConversationTypeLabelPipe,
		FormatChatTimePipe,
	],
})
export class ChatMessageView {
	readonly #chatState = inject(ChatState)
	readonly #chatService = inject(ChatService)
	readonly #operatorDataState = inject(OperatorDataState)
	readonly #destroyRef = inject(DestroyRef)

	readonly messageListRef = viewChild<ElementRef<HTMLUListElement>>('messageList')

	readonly refresh = output<void>()

	readonly conversation = this.#chatState.activeConversation
	readonly messages = this.#chatState.messages
	readonly isLoading = this.#chatState.isLoadingMessages
	readonly isSending = signal(false)

	readonly messageControl = new FormControl<string>('', [Validators.required, Validators.minLength(1)])

	constructor() {
		effect(() => {
			this.messages()
			this.#scrollToBottom()
		})
	}

	markAsRead(): void {
		const uuid = this.#chatState.activeConversationUuid()
		const requestPointUuid = this.#operatorDataState.metadata()?.requestPointUuid ?? ''

		if (!uuid) return

		this.#chatService.markAsRead(uuid, requestPointUuid).pipe(
			takeUntilDestroyed(this.#destroyRef)
		).subscribe()
	}

	sendMessage(): void {
		const content = this.messageControl.value?.trim()

		if (!content || this.isSending()) return

		const uuid = this.#chatState.activeConversationUuid()
		const requestPointUuid = this.#operatorDataState.metadata()?.requestPointUuid ?? ''

		if (!uuid) return

		this.isSending.set(true)
		this.messageControl.disable()

		this.#chatService.sendMessage({ conversationUuid: uuid, senderRequestPointUuid: requestPointUuid, content }).pipe(
			finalize(() => {
				this.messageControl.enable()
				this.isSending.set(false)
			}),
			takeUntilDestroyed(this.#destroyRef)
		).subscribe(() => {
			this.messageControl.reset()
			this.refresh.emit()
		})
	}

	handleEnterKey(event: KeyboardEvent): void {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault()
			this.sendMessage()
		}
	}

	receiptStatusClass(status: ChatReceiptStatus): string {
		const map: Record<ChatReceiptStatus, string> = {
			PENDING: 'status-pending',
			SENT: 'status-sent',
			DELIVERED: 'status-delivered',
			READ: 'status-read',
		}

		return map[status]
	}

	#scrollToBottom(): void {
		queueMicrotask(() => {
			const el = this.messageListRef()?.nativeElement
			if (!el) return
			el.scrollTop = el.scrollHeight
		})
	}
}
